import React, { useCallback, useEffect, useState } from "react";

import { CircularProgressbar } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { calculateTickByMacroScore } from "../../utils/ticks";

import * as SC from "./ScoreCircularProgress.styles";

type ScoreCircularProgressProps = {
  hideScore?: boolean;
  score?: number;
  size?: number;
  width?: string;
  height?: string;
  strokeWidth?: number;
  disabled?: boolean;
  minted?: boolean;
  loading?: boolean;
  showGrade?: boolean;
  tryAgain?: boolean;
  pathColor?: string;
  lineColor?: string;
};

const ScoreCircularProgress: React.FC<ScoreCircularProgressProps> = ({
  score,
  size,
  width,
  height,
  strokeWidth = 10,
  disabled,
  minted,
  hideScore,
  loading,
  showGrade,
  tryAgain,
  pathColor,
  lineColor,
}: ScoreCircularProgressProps) => {
  const [progress, setProgress] = useState(0);
  const [circleColor, setCircleColor] = useState<string>();
  const [tick, setTick] = useState<number>(0); // just for testnet until api properly returns it

  if (size) {
    width = size + "px";
    height = size + "px";
  }

  const calculateFinalScore = (): void => {
    const finalScore =
      score === null || score?.toString() === "0"
        ? localStorage.getItem("score")
        : score;
    if (!finalScore) return;
    setProgress(Number(finalScore));
    const tick = calculateTickByMacroScore(Number(finalScore));
    setTick(tick);
  };

  useEffect(() => {
    calculateFinalScore();
  }, [score]);

  useEffect(() => {
    calculateFinalScore();
  }, []);

  useEffect(() => {
    getCircleColor();
  }, [tick]);

  const getCircleColor = useCallback(() => {
    switch (tick) {
      case 1:
        setCircleColor("#FC695C");
        return;
      case 2:
        setCircleColor("#FD9858");
        return;
      case 3:
        setCircleColor("#E9DA61");
        return;
      case 4:
        setCircleColor("#8BDA9C");
        return;
      case 5:
        setCircleColor("#8BDA9C");
        // setCircleColor("#54B268");
        return;
      default:
        setCircleColor("#FC695C");
        return;
    }
  }, [tick]);

  return (
    <SC.WrapperHover
      width={width as string}
      height={height as string}
      showGrade={showGrade}
    >
      <SC.Wrapper showGrade={showGrade}>
        <SC.Container>
          <CircularProgressbar
            value={progress}
            maxValue={850}
            strokeWidth={strokeWidth}
            background
            // eslint-disable-next-line no-nested-ternary
            text={
              tryAgain
                ? "Try Again"
                : loading
                ? "Loading..."
                : hideScore
                ? ""
                : disabled
                ? "?"
                : parseInt(
                    score?.toString() || localStorage.getItem("score") || "0"
                  ).toString()
            }
            styles={{
              path: {
                stroke: pathColor || (disabled ? "#484c50" : circleColor),
                transform: "rotate(0.5turn)",
                transformOrigin: "center center",
                strokeWidth: strokeWidth,
              },
              trail: {
                stroke: minted ? "#E5EBF3" : lineColor || "#E5EBF3",
                strokeWidth: strokeWidth * 0.5,
                opacity: 0.7,
              },
              background: {
                fill: "transparent",
              },
              text: {
                fill: minted ? "#DDE5F2" : "#221D3C",
                fontSize: minted ? 28 : loading ? "12px" : "24px",
                fontWeight: "bold",
                dominantBaseline: "central",
              },
            }}
          />
        </SC.Container>

        {showGrade && (
          <SC.Container backface>
            <SC.Grade color={circleColor || "#647693"}>
              {"FEDCBA"[tick]}
              <span>Your Grade</span>
            </SC.Grade>
          </SC.Container>
        )}
      </SC.Wrapper>
    </SC.WrapperHover>
  );
};

ScoreCircularProgress.defaultProps = {
  score: 0,
  width: "160px",
  height: "160px",
  disabled: false,
  minted: false,
  hideScore: false,
  loading: false,
};

export default ScoreCircularProgress;
